import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import UnderageNoticeDialog from '../shared/UnderageNoticeDialog';
import { isUnderVoterAge } from '../shared/voterAgeRules';
import VoterInclusionPreviewFrame from './VoterInclusionPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import VoterInclusionFormInfoBox from './VoterInclusionFormInfoBox';
import {
    INITIAL_VOTER_INCLUSION_FORM,
    PROOF_KEYS,
    ageOnRollDate,
    buildVoterInclusionForm,
    displayField,
    formatDateField,
    getEmptyVoterInclusionFields,
    getMobileValidationError,
    getVoterInclusionFormSnapshot,
    isVoterInclusionFormComplete,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    saveVoterInclusionFormSnapshot,
    toBengaliDigits,
} from './voterInclusionFormEngine';
import { downloadVoterInclusionPdf, printVoterInclusionForm } from './generateVoterInclusionPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';

/** Calendar range: old enough for any voter's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-3.5 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {children}
        </div>
    );
}

/** Plain field — no datalist here: the office data is one-off, not reusable. */
function FormInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass}`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function VoterInclusionFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_VOTER_INCLUSION_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Set when a birth date under eighteen needs pointing out — on picking the
    // date, and again before the form leaves as a PDF or a print.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [mobileError, setMobileError] = useState('');
    const [mobileTouched, setMobileTouched] = useState(false);
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getVoterInclusionFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.applicantName)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveVoterInclusionFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
    }, []);

    const handleDigitsChange = (key, raw, maxLength) => {
        update(key, toBengaliDigits(normalizeDigits(raw, maxLength)));
    };

    const handleMobileChange = (raw) => {
        const digits = normalizeBdPhone(raw);
        update('mobile', digits);
        if (mobileTouched) setMobileError(getMobileValidationError(digits, t));
    };

    const validateMobile = () => {
        setMobileTouched(true);
        if (!form.mobile) {
            setMobileError('');
            return;
        }
        setMobileError(getMobileValidationError(form.mobile, t));
    };

    /**
     * Remember what was typed, then export — whatever state the form is in.
     * Missing fields do not block the download: printing the form half-filled to
     * finish by hand at the office is a normal way to use it. Downloading asks
     * for a confirmation that names what is still empty; printing does not.
     */
    const prepareForExport = () => {
        saveVoterInclusionFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptyVoterInclusionFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterInclusionForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/voter-inclusion-form', { category: 'general' });
            const base = form.applicantName.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadVoterInclusionPdf(previewRef.current, `voter-inclusion-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('voterInclusionForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterInclusionForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    const birthDate = parseDateField(form.birthDate);
    const underVoterAge = isUnderVoterAge(birthDate);

    const handleDownloadPdf = () => {
        if (underVoterAge) {
            setAgeNotice({ mode: 'download', date: birthDate });
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = () => {
        if (underVoterAge) {
            setAgeNotice({ mode: 'print', date: birthDate });
            return;
        }
        void runPrint();
    };

    const runPrint = async () => {
        recordToolUsage('general/voter-inclusion-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('voterInclusionForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printVoterInclusionForm(previewRef.current, t('voterInclusionForm.errors.popupBlocked'));
            notifications.show({
                title: t('voterInclusionForm.printTitle'),
                message: t('voterInclusionForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('voterInclusionForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_VOTER_INCLUSION_FORM);
        setMobileError('');
        setMobileTouched(false);
        notifications.show({
            title: t('voterInclusionForm.clearedTitle'),
            message: t('voterInclusionForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getVoterInclusionFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('voterInclusionForm.noSnapshotTitle'),
                message: t('voterInclusionForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setMobileError('');
        setMobileTouched(false);
        notifications.show({
            title: t('voterInclusionForm.restoredTitle'),
            message: t('voterInclusionForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => (
            key !== 'ageYear' && displayField(value)
        )),
        [form],
    );

    const isComplete = useMemo(() => isVoterInclusionFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('voterInclusionForm.statusReady'),
        success: t('voterInclusionForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`voterInclusionForm.howTo.step${i}`)),
        [t],
    );

    /** Age as the form will print it, so the applicant sees what the sheet says. */
    const printed = useMemo(() => buildVoterInclusionForm(form), [form]);
    const age = ageOnRollDate(parseDateField(form.birthDate), form.ageYear);

    const geoField = (key, geo, { selectOnly = false } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterInclusionForm.fields.${key}`)}</FormLabel>
            <GeoSelectField
                {...geo}
                selectOnly={selectOnly}
                placeholder={t(`voterInclusionForm.placeholders.${key}`)}
                inputClassName={`${inputBaseClass} ${inputOkClass}`}
            />
        </div>
    );

    // District comes first in each block so the upazila list can follow it.
    const officeGeo = geoCascade(form, update, { district: 'officeDistrict', upazila: 'officeUpazila' });
    const addressGeo = geoCascade(form, update, { division: 'division', district: 'district', upazila: 'thana' });

    const textField = (key, extra = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterInclusionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                placeholder={t(`voterInclusionForm.placeholders.${key}`)}
                {...extra}
            />
        </div>
    );

    const digitField = (key, maxLength, extra = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterInclusionForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={toBengaliDigits(form[key])}
                onChange={(v) => handleDigitsChange(key, v, maxLength)}
                placeholder={t(`voterInclusionForm.placeholders.${key}`)}
                inputMode="numeric"
                maxLength={maxLength}
                {...extra}
            />
        </div>
    );

    /** Dates are picked, so they always come out দিন-মাস-বছর in Bengali digits. */
    const dateField = (key, { fromYear = 1950 } = {}) => (
        <div>
            <FormLabel htmlFor={key}>{t(`voterInclusionForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => {
                    update(key, formatDateField(date));
                    if (key === 'birthDate' && isUnderVoterAge(date)) setAgeNotice({ mode: 'picked', date });
                }}
                formatValue={formatDateField}
                placeholder={t(`voterInclusionForm.placeholders.${key}`)}
                triggerClassName={`${inputBaseClass} ${inputOkClass}`}
                fromYear={fromYear}
                toYear={TODAY.getFullYear()}
                disabled={{ after: TODAY }}
                clearable
            />
        </div>
    );

    return (
        <>
        <UnderageNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={ageNotice?.date ?? null}
            confirmLabel={ageNotice?.mode === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice?.mode === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice?.mode;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/voter-inclusion-form"
            subtitleKey="voterInclusionForm.subtitle"
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('voterInclusionForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('voterInclusionForm.clearAll')}
            primaryAction={{
                label: t('voterInclusionForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('voterInclusionForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('voterInclusionForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('voterInclusionForm.sections.office.number')}
                        title={t('voterInclusionForm.sections.office.title')}
                    >
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {geoField('officeDistrict', officeGeo.district, { selectOnly: true })}
                            {geoField('officeUpazila', officeGeo.upazila)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.applicant.number')}
                        title={t('voterInclusionForm.sections.applicant.title')}
                    >
                        <div className="space-y-3">
                            {textField('applicantName', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('fatherName')}
                                {textField('motherName')}
                            </div>
                            {textField('spouseName')}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.address.number')}
                        title={t('voterInclusionForm.sections.address.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('division', addressGeo.division, { selectOnly: true })}
                                {geoField('district', addressGeo.district, { selectOnly: true })}
                            </div>
                            {geoField('thana', addressGeo.upazila)}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('union')}
                                {textField('ward')}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('postOffice', { autoComplete: 'address-line2' })}
                                {textField('village', { autoComplete: 'street-address' })}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('road')}
                                {textField('houseNo')}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.stay.number')}
                        title={t('voterInclusionForm.sections.stay.title')}
                    >
                        <div className="space-y-3">
                            {textField('notIncludedReason')}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('stayYears', 3)}
                                {digitField('stayMonths', 2)}
                            </div>
                            <div>
                                <FormLabel>{t('voterInclusionForm.fields.proofs')}</FormLabel>
                                <div className="flex flex-wrap gap-x-4 gap-y-2">
                                    {PROOF_KEYS.map((key) => (
                                        <label
                                            key={key}
                                            className="flex cursor-pointer items-center gap-2 text-[13px] text-[#3D3833] dark:text-zinc-200"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={Boolean(form[key])}
                                                onChange={(e) => update(key, e.target.checked ? 'yes' : '')}
                                                className="h-4 w-4 accent-purple-600"
                                            />
                                            {t(`voterInclusionForm.proofs.${key}`)}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.age.number')}
                        title={t('voterInclusionForm.sections.age.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {dateField('birthDate', { fromYear: BIRTH_FROM_YEAR })}
                                {digitField('ageYear', 4)}
                            </div>
                            {age ? (
                                <p className="rounded-lg bg-purple-50 px-3 py-2 text-[12.5px] text-purple-800 dark:bg-purple-900/20 dark:text-purple-200">
                                    {t('voterInclusionForm.ageResult', {
                                        year: toBengaliDigits(form.ageYear),
                                        years: printed.ageYears,
                                        months: printed.ageMonths,
                                        days: printed.ageDays,
                                    })}
                                    {age.years < 18 ? ` ${t('voterInclusionForm.ageUnder18')}` : ''}
                                </p>
                            ) : (
                                <p className="text-[12px] text-[#8A8178] dark:text-zinc-400">
                                    {t('voterInclusionForm.ageHint')}
                                </p>
                            )}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.authority.number')}
                        title={t('voterInclusionForm.sections.authority.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {dateField('applicationDate')}
                                {dateField('authorizationDate')}
                            </div>
                            {textField('representativeName', { autoComplete: 'off' })}
                            {textField('representativeFather', { autoComplete: 'off' })}
                            {textField('representativeAddress', { autoComplete: 'off' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('voterInclusionForm.sections.declaration.number')}
                        title={t('voterInclusionForm.sections.declaration.title')}
                    >
                        <div>
                            <FormLabel htmlFor="mobile">{t('voterInclusionForm.fields.mobile')}</FormLabel>
                            <FormInput
                                id="mobile"
                                name="mobile"
                                value={form.mobile}
                                onChange={handleMobileChange}
                                onBlur={validateMobile}
                                placeholder={t('voterInclusionForm.placeholders.mobile')}
                                error={mobileTouched ? mobileError : ''}
                                inputMode="tel"
                                type="tel"
                                maxLength={11}
                                autoComplete="tel"
                            />
                        </div>
                    </FormSection>
                </div>
            )}
            preview={(
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('voterInclusionForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('voterInclusionForm.previewEmpty')}
                        idleHint={t('voterInclusionForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('voterInclusionForm.livePreview')}
                            </div>
                            <VoterInclusionPreviewFrame form={form} previewRef={previewRef} zoom={pageZoom.zoom} />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<VoterInclusionFormInfoBox />}
        />
        </>
    );
}
